import type { Apis, SessionService } from '@efdigital/ll-addon-auth';
import {
  canTransitionToStatus,
  type CoachingPartnershipTransitionDecision,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/coach-partnership-status';
import {
  isCancelled,
  isCompleted,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-status';
import {
  type CoachingPartnership,
  type CoachingPartnershipStatus,
  type CoachingPlan,
  type CoachingSession,
  type CoachingSessionType,
} from '@efdigital/ll-addon-data';
import type {
  CoacheeGetQuotaResponseBody,
  CoacheePostAttendanceMarkRequestBody,
} from '@efdigital/ll-cdk-coaching-sessions-api-types';
import { queryCoachingPartnershipsByCoachingPlan } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-partnerships.ts';
import { queryCoachingSessionsByCoachingPlan } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-sessions.ts';
import {
  buildCoacheeGetQuotaRequest,
  parseCoacheeGetQuotaResponse,
} from '@efdigital/ll-xceed-v2/data/builders/coaching/sessions.ts';
import type CoacheeService from '@efdigital/ll-xceed-v2/services/coachee.ts';
import { cached, tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';

export type CoachingPlannerProps = {
  store: Store;
  coachee: CoacheeService;
  session: SessionService;
  apis: Apis;
};

export class CoachingPlanner {
  @tracked coachingPlan: CoachingPlan;
  @tracked allPartnerships: CoachingPartnership[];
  @tracked allSessions: CoachingSession[];
  @tracked quota: CoacheeGetQuotaResponseBody;

  private constructor(
    private props: CoachingPlannerProps,
    coachingPlan: CoachingPlan
  ) {
    this.coachingPlan = coachingPlan;
    this.allPartnerships = [];
    this.allSessions = [];
    this.quota = {
      coachingPlanId: coachingPlan.id!,
      chemistry: {
        allocated: 0,
        remaining: 0,
      },
      coaching: {
        allocated: 0,
        remaining: 0,
      },
    };
  }

  ///// --- Data loading

  static async load(
    coachingPlan: CoachingPlan,
    props: CoachingPlannerProps
  ): Promise<CoachingPlanner> {
    const manager = new CoachingPlanner(props, coachingPlan);
    await manager.#reloadPartnerships();
    await manager.#reloadSessionsAndQuota();
    return manager;
  }

  async #reloadPartnerships() {
    const coachingPartnershipQuery = queryCoachingPartnershipsByCoachingPlan(
      this.coachingPlan.id!
    );
    const findExisting = await this.props.store.request(
      coachingPartnershipQuery
    );
    this.allPartnerships = findExisting.content.data;
  }

  async #reloadSessionsAndQuota() {
    const querySessions = queryCoachingSessionsByCoachingPlan(
      this.coachingPlan.id!
    );
    const sessions = await this.props.store.request(querySessions);

    const queryQuota = await buildCoacheeGetQuotaRequest(
      this.props.apis,
      this.props.session,
      this.coachingPlan.id!
    );

    const response = await fetch(queryQuota);
    const quota = await parseCoacheeGetQuotaResponse(response);

    this.allSessions = sessions.content.data;
    this.quota = quota;
  }

  /// ---- Business logic
  canTransitionToStatus = (
    coachUserId: string,
    nextStatus: CoachingPartnershipStatus
  ): CoachingPartnershipTransitionDecision => {
    const currentStatus =
      this.allPartnerships.find(
        (partnership) => partnership.coachUserId === coachUserId
      )?.status ?? 'pending';

    const decision = canTransitionToStatus(
      currentStatus,
      nextStatus,
      this.coachHasBeenChosen
    );

    if (decision.outcome === 'blocked' || decision.outcome === 'no-op') {
      return decision;
    }

    if (currentStatus === 'chemistry' && nextStatus !== 'chosen') {
      if (this.hasUncancelledChemistrySession(coachUserId)) {
        return {
          outcome: 'blocked',
          reason:
            'Cannot remove from shortlist once a chemistry session has been booked',
        };
      }
    }

    if (nextStatus === 'chemistry') {
      if (
        !this.hasUncancelledChemistrySession(coachUserId) &&
        !this.hasChemistryQuota
      ) {
        // We will allow someone to re-shortlist a coach if they have previously had a chemistry session
        // as they might have changed their mind about the coach they want to work with
        return {
          outcome: 'blocked',
          reason:
            'Not enough chemistry quota remaining to shortlist more coaches',
        };
      }
    }

    if (nextStatus === 'chosen') {
      if (!this.hasCompletedChemistrySession(coachUserId)) {
        return {
          outcome: 'blocked',
          reason: 'Must book chemistry session first',
        };
      }
    }

    return decision;
  };

  getChemistrySessions = (coachUserId: string): CoachingSession[] => {
    return this.allSessions.filter(
      (session) =>
        session.coachUserId === coachUserId &&
        session.sessionType === 'chemistry'
    );
  };

  getStatusForCoach = (coachUserId: string) => {
    return (
      this.allPartnerships.find(
        (partnership) => partnership.coachUserId === coachUserId
      )?.status ?? 'pending'
    );
  };

  canBookChemistrySession = (coachUserId: string) => {
    return (
      !this.coachHasBeenChosen &&
      this.getStatusForCoach(coachUserId) === 'chemistry' &&
      this.hasChemistryQuota &&
      !this.hasUncancelledChemistrySession(coachUserId)
    );
  };

  canBookCoachingSession = (coachUserId: string) => {
    return (
      this.coachHasBeenChosen &&
      this.getStatusForCoach(coachUserId) === 'chosen' &&
      this.hasCoachingQuota
    );
  };

  get hasChemistryQuota() {
    return this.quota.chemistry.remaining > 0;
  }

  get hasCoachingQuota() {
    return this.quota.coaching.remaining > 0;
  }

  @cached
  get chemistryAndChosenPartnerships() {
    return [
      ...this.#partnershipsWithStatus('chosen'),
      ...this.#partnershipsWithStatus('chemistry'),
    ];
  }

  @cached
  get chosenPartnerships() {
    return this.#partnershipsWithStatus('chosen');
  }

  #partnershipsWithStatus(status: CoachingPartnershipStatus) {
    return this.allPartnerships.filter(
      (partnership) => partnership.status === status
    );
  }

  hasUncancelledChemistrySession = (coachUserId: string) => {
    return this.allSessions.some(
      (session) =>
        session.coachUserId === coachUserId &&
        session.sessionType === 'chemistry' &&
        !isCancelled(session.status) &&
        session.status !== 'booking_abandoned'
    );
  };

  hasCompletedChemistrySession = (coachUserId: string) => {
    return this.allSessions.some(
      (session) =>
        session.coachUserId === coachUserId &&
        session.sessionType === 'chemistry' &&
        isCompleted(session.status)
    );
  };

  chosenCoachPartnership = () => {
    return this.allPartnerships.find(
      (partnership) => partnership.status === 'chosen'
    );
  };

  get coachHasBeenChosen() {
    return this.allPartnerships.some(
      (partnership) => partnership.status === 'chosen'
    );
  }

  // ------ Actions

  bookSession = async (
    coachUserId: string,
    sessionType: CoachingSessionType,
    idempotencyKey: string,
    startTime: string,
    endTime: string
  ) => {
    const partnership = this.allPartnerships.find(
      (partnership) => partnership.coachUserId === coachUserId
    )!;

    await this.props.coachee.bookSession(
      partnership,
      sessionType,
      idempotencyKey,
      startTime,
      endTime
    );

    await this.#reloadSessionsAndQuota();
  };

  cancelSession = async (
    coachingSession: CoachingSession,
    cancelReason: string
  ) => {
    await this.props.coachee.cancelSession(coachingSession, cancelReason);

    await this.#reloadSessionsAndQuota();
  };

  setStatusForCoach = async (
    coachUserId: string,
    status: CoachingPartnershipStatus
  ) => {
    const hadExistingPartnership = this.allPartnerships.some(
      (partnership) => partnership.coachUserId === coachUserId
    );

    await this.props.coachee.setStatusForCoach(
      this.coachingPlan,
      coachUserId,
      status
    );

    if (!hadExistingPartnership) {
      await this.#reloadPartnerships();
    }
  };

  markAttendance = async (
    coachingSession: CoachingSession,
    body: CoacheePostAttendanceMarkRequestBody
  ) => {
    await this.props.coachee.markAttendance(coachingSession, body);

    await this.#reloadSessionsAndQuota();
  };
}
